import { getConfigForImportedTest, getConfigForTest } from 'Components/Worksheets/Config';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import qestIds from '../../../QestIds';
import getDerivedFromMeasurementsConfig from '../../derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

const config = (_: string) =>
  mergeConfig(
    {
      ...getConfigForTest(
        qestIds.moistureContent.AS_1289_2_1_1.rev2005,
        'data.OversizeCorrectionData.OversizeCorrectionConstantDryMass'
      ),
      'data.OversizeCorrectionData.OversizeVolume': { uom: 'cm³', format: { nearest: 0.01 } },
      'data.OversizeCorrectionData.OversizeDensity': { uom: 't/m³', format: { nearest: 0.01 } },

      'data.OversizeCorrectionData.TareAndTotalSampleWetMass': {
        uom: 'g',
        format: { nearest: 1 },
      },

      //Constant Dry Mass fields. Overrides the default config
      'data.OversizeCorrectionData.OversizeCorrectionConstantDryMass.DryingTray.DryMassReadings.DrySoilAndContainerMass':
        {
          uom: 'g',
          format: { nearest: 1 },
        },
      'data.OversizeCorrectionData.OversizeCorrectionConstantDryMass.DryingTray.WetSoilMass': {
        uom: 'g',
        format: { nearest: 1 },
      },
      'data.OversizeCorrectionData.OversizeCorrectionConstantDryMass.DryingTray.ContainerMass': {
        uom: 'g',
        format: { nearest: 1 },
      },
      'data.OversizeCorrectionData.OversizeCorrectionConstantDryMass.DryingTray.WetSoilAndContainerMass': {
        uom: 'g',
        format: { nearest: 1 },
      },

      //Field Results
      'data.FieldResults.FieldWetDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.FieldResults.FieldMoistureContent': { format: { nearest: 0.1 } },
      'data.FieldResults.AssociatedFieldWetDensity.ExternalTest.Results.FieldWetDensity': {
        uom: 't/m³',
        format: { nearest: 0.01 },
      },
      'data.FieldResults.AssociatedFieldWetDensity.ExternalTest.Results.FieldMoistureContent': {
        format: { nearest: 0.1 },
      },
      ...getConfigForImportedTest(
        qestIds.nuclearFieldDensity.AS_1289_5_8_1.rev2007,
        'data.FieldResults.AssociatedFieldWetDensity'
      ),
      'data.FieldResults.AssociatedLabMoistureContent.ExternalTest.Results.MoistureContentPercent': {
        format: { nearest: 0.1 },
      },
      ...getConfigForImportedTest(
        qestIds.moistureContent.AS_1289_2_1_1.rev2005,
        'data.FieldResults.AssociatedLabMoistureContent'
      ),
      'data.FieldResults.LabMoistureContent': {
        format: { nearest: 0.1 },
      },
      'data.FieldResults.TareMass': { uom: 'g', format: { nearest: 0.1 } },
      'data.FieldResults.TotalWetSampleMass': { uom: 'g', format: { nearest: 0.1 } },
      'data.FieldResults.OversizeMaterialWetMass': { uom: 'g', format: { nearest: 0.1 } },
      'data.FieldResults.MaximumDryDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.FieldResults.OptimumMoistureContent': { format: { nearest: 0.1 } },
      'data.FieldResults.TimeBetweenAdditionOfStabiliserAndCompaction': {
        uom: 'h',
        format: { nearest: 0.1 },
      },
      'data.FieldResults.AssociatedCompactionData.ExternalTest.Results.TimeBetweenAdditionOfStabiliserAndCompaction': {
        uom: 'h',
        format: { nearest: 0.1 },
      },
      'data.FieldResults.AssociatedCompactionData.ExternalTest.Results.OptimumMoistureContent': {
        format: { nearest: 0.1 },
      },
      'data.FieldResults.AssociatedCompactionData.ExternalTest.Results.MaximumDryDensity': {
        uom: 't/m³',
        format: { nearest: 0.01 },
      },
      'data.FieldResults.AssociatedCompactionData.ExternalTest.Results.OversizeSieveApertureSize': {
        uom: 'mm',
        format: { nearest: 0.1 },
      },

      'data.Results.MoistureVariation': { format: { nearest: 0.1 } },
      'data.Results.OptimumMoistureContent': { format: { nearest: 0.1 } },
      'data.Results.AdjustedOptimumMoistureContent': { format: { nearest: 0.1 } },
      'data.Results.MoistureRatio': { format: { nearest: 0.1 } },
      'data.Results.OversizeWet': { format: { nearest: 1 } },
      'data.Results.OversizeDry': { format: { nearest: 1 } },
      'data.Results.TotalSampleDryMass': { uom: 'g', format: { nearest: 5 } },
      'data.Results.DryDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.Results.MaximumDryDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.Results.AdjustedMaximumDryDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.Results.DryDensityRatio': { format: { nearest: 0.1 } },
      'data.Results.ResultMoistureContent': { format: { nearest: 0.1 } },
      'data.Results.FieldWetDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.OversizeCorrectionData.OversizeCorrectionConstantDryMass.DryingTray.DryMassBalance': {
        defaultReadability: 1,
        defaultUom: 'g',
      },
      'data.OversizeCorrectionData.Balance': { defaultReadability: 1, defaultUom: 'g' },
    },
    getLabelConfig('data'),
    getMeasuredUsingConfig('data'),
    getDerivedFromMeasurementsConfig('data')
  );

export default {
  [qestIds.relativeCompaction.AS_1289_5_4_1.rev2007]: config,
};
